<template>
  <div class="download-page">
    <!-- 顶部 Hero 区域 -->
    <section class="hero">
      <div class="hero-inner">
        <h1 class="hero-title">T-viewer 病理图像处理软件</h1>
        <p class="hero-desc">
          T-viewer 是一款为病理科室打造的专业可视化工具，支持对病理图像进行浏览、标记、分析和分享，
          供医务人员临床病理疾病诊断参考，不含自动诊断功能。
        </p>
        <div class="hero-actions">
          <button class="btn btn-primary" @click="scrollToDownload">
            立即获取 T-viewer
          </button>
          <button class="btn btn-secondary" @click="scrollToManual">
            了解使用手册
          </button>
        </div>
      </div>
    </section>

    <!-- 主要功能 -->
    <section class="section section-features">
      <div class="section-inner">
        <h2 class="section-title">T-viewer 主要功能</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-header">
              <img class="feature-icon" src="@/../static/iamges/tviewer/eye.svg">
              <h3 class="feature-title">病理图像浏览</h3>
            </div>
            <p class="feature-text">
              支持大图快速缩放、平移与多层级查看，流畅查看高分辨率病理切片。
              支持分屏多页不同切片同时查看。
            </p>
          </div>
          <div class="feature-card">
            <div class="feature-header">
              <img class="feature-icon" src="@/../static/iamges/tviewer/edit.svg">
              <h3 class="feature-title">标记与注释</h3>
            </div>
            <p class="feature-text">
              提供多种标记工具，可对重点区域进行圈选、标注与说明，便于病例沟通与教学。
            </p>
          </div>
          <div class="feature-card">
            <div class="feature-header">
              <img class="feature-icon" src="@/../static/iamges/tviewer/aiAssistant.svg">
              <h3 class="feature-title">AI辅助分析</h3>
            </div>
            <p class="feature-text">
              集成AI辅助分析功能，协助完成病理分析，为诊断提供更多参考信息。
            </p>
          </div>
          <div class="feature-card">
            <div class="feature-header">
              <img class="feature-icon" src="@/../static/iamges/tviewer/collaboration.svg">
              <h3 class="feature-title">多端协作</h3>
            </div>
            <p class="feature-text">
              支持病例分享与协作查看，方便团队讨论与多学科会诊，提升沟通效率。
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 界面预览 / 截图 -->
    <section class="section section-preview" style="padding-bottom: 0px;">
      <div class="section-inner">
        <div class="carousel-wrapper">
          <el-carousel
            height="400px"
            type=""
            :interval="3000"
            trigger="click"
          >
            <el-carousel-item v-for="(item, index) in carouselImages" :key="index">
              <img
                :src="item.src"
                :alt="item.alt"
                class="carousel-image"
              />
            </el-carousel-item>
          </el-carousel>
        </div>
      </div>
    </section>

    <!-- 下载中心 -->
    <section class="section section-download" ref="downloadSection">
      <div class="section-inner">
        <h2 class="section-title">下载中心</h2>
        <p class="section-subtitle">
          选择适合您的系统版本，快速安装并开始使用 T-viewer。
        </p>

        <div class="download-grid">
          <!-- 软件安装包 -->
          <div class="download-card">
            <div class="download-header">
              <div class="download-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-download w-8 h-8" aria-hidden="true" data-md-id="src/pages/Download.tsx:123:20" data-md-name="svg" data-component-is-root="false" data-component-path="src/pages/Download.tsx" data-component-line="123" data-component-end-line="123" data-component-file="Download.tsx" data-component-name="svg" data-component-content="%7B%22svg%22%3A%22%3CDownload%20className%3D%5C%22w-8%20h-8%5C%22%20%2F%3E%22%2C%22allowLink%22%3Atrue%7D"><path d="M12 15V3"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><path d="m7 10 5 5 5-5"></path></svg>
              </div>
              <h3 class="download-title">T-viewer 软件(基础版)</h3>
            </div>
            <p class="download-subtitle">支持 Windows 10 / 11 64位 操作系统</p>
            <ul class="download-meta">
              <li>基础版没有AI高清化功能</li>
              <li>适用场景：临床病理诊断、教学示教、科研分析</li>
            </ul>
            <button class="btn btn-primary full-width" @click="onDownloadSoftware">
              立即下载安装包
            </button>
            <p class="download-extra">
              安装前建议先阅读使用手册，了解环境要求与安装步骤。
            </p>
          </div>

          <!-- 软件安装包 -->
          <div class="download-card">
            <div class="download-header">
              <div class="download-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-download w-8 h-8" aria-hidden="true" data-md-id="src/pages/Download.tsx:123:20" data-md-name="svg" data-component-is-root="false" data-component-path="src/pages/Download.tsx" data-component-line="123" data-component-end-line="123" data-component-file="Download.tsx" data-component-name="svg" data-component-content="%7B%22svg%22%3A%22%3CDownload%20className%3D%5C%22w-8%20h-8%5C%22%20%2F%3E%22%2C%22allowLink%22%3Atrue%7D"><path d="M12 15V3"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><path d="m7 10 5 5 5-5"></path></svg>
              </div>
              <h3 class="download-title">T-viewer软件(带HD版本)</h3>
            </div>
            <p class="download-subtitle">支持 Windows 10 / 11 64位 操作系统</p>
            <ul class="download-meta">
              <li>有AI高清化功能，开启高清化功能需要NVIDIA显卡驱动版本大于等于560</li>
              <li>升级更新时，无需卸载，只需要下载并覆盖安装基础版</li>
            </ul>
            <button class="btn btn-primary full-width" @click="onDownloadSoftwareHD">
              立即下载安装包
            </button>
            <p class="download-extra">
              安装前建议先阅读使用手册，了解环境要求与安装步骤。
            </p>
          </div>

          <!-- 使用手册 -->
          <div class="download-card" ref="manualSection">
            <div class="download-header">
              <div class="download-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-book-open w-8 h-8" aria-hidden="true" data-md-id="src/pages/Download.tsx:149:20" data-md-name="svg" data-component-is-root="false" data-component-path="src/pages/Download.tsx" data-component-line="149" data-component-end-line="149" data-component-file="Download.tsx" data-component-name="svg" data-component-content="%7B%22svg%22%3A%22%3CBookOpen%20className%3D%5C%22w-8%20h-8%5C%22%20%2F%3E%22%2C%22allowLink%22%3Atrue%7D"><path d="M12 7v14"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path></svg>
              </div>
              <h3 class="download-title">使用手册</h3>
            </div>
            <p class="download-subtitle">T-viewer 用户使用指南（PDF）</p>
            <ul class="download-meta">
              <li>内容涵盖：功能介绍、典型操作流程、常见问题说明</li>
              <li>适用对象：病理医师、技师及相关科研人员</li>
            </ul>
            <button class="btn btn-secondary full-width" @click="onDownloadManual">
              下载 PDF 手册
            </button>
            <p class="download-extra">
              建议新用户在首次使用前完整阅读，便于快速上手并规范使用。
            </p>
          </div>
        </div>

        <!-- <p class="disclaimer">
          T-viewer 仅作为病理图像浏览、标记、分析和分享的辅助工具，
          供医务人员临床病理疾病诊断参考，不具备也不提供任何自动诊断功能。
          任何诊断结论和医疗决策应由具备相应资质的专业医务人员独立作出。
        </p> -->
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'download',
  data() {
    return {
      // 轮播图图片，静态资源放在 static/iamges/tviewer 下
      carouselImages: [
        {
          src: require('@/../static/iamges/tviewer/display1.png'),
          alt: 'T-viewer 界面展示 1'
        },
        {
          src: require('@/../static/iamges/tviewer/display2.png'),
          alt: 'T-viewer 界面展示 2'
        },
        {
          src: require('@/../static/iamges/tviewer/display3.png'),
          alt: 'T-viewer 界面展示 3'
        },
        {
          src: require('@/../static/iamges/tviewer/display4.png'),
          alt: 'T-viewer 界面展示 4'
        }
      ]
    }
  },
  methods: {
    scrollToDownload() {
      const el = this.$refs.downloadSection
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        window.location.hash = '#/download'
      }
    },
    scrollToManual() {
      const el = this.$refs.manualSection
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else {
        this.scrollToDownload()
      }
    },
    onDownloadSoftware() {
      // TODO: 替换为真实软件下载地址
      const url = 'https://pan.baidu.com/s/5CIDAI2PB0wvk4cOYn48tTA'
      window.open(url, '_blank')
    },
    onDownloadSoftwareHD() {
      // TODO: 替换为真实软件下载地址
      const url = 'https://pan.baidu.com/s/5D7Z-WxIUdsr2ilfe_Lh0GQ'
      window.open(url, '_blank')
    },
    onDownloadManual() {
      // TODO: 替换为真实使用手册地址
      const url = 'https://scan.turingene.com/T-viewer-Instructions/T-viewer-Instructions.pdf'
      window.open(url, '_blank')
    }
  }
}
</script>

<style scoped>
.download-page {
  min-height: 100vh;
  background: #f5f7fb;
  color: #1f2933;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei',
    'Helvetica Neue', Arial, sans-serif;
  text-align: left;
}

/* 顶部 Hero 区域 */
.hero {
  background: linear-gradient(180deg, #e7f3d4 0%, #F5F7FB 100%);
  padding: 64px 16px 40px;
}

.hero-inner {
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
}

.hero-subtitle {
  font-size: 14px;
  color: #4f5b66;
  margin-bottom: 8px;
}

.hero-title {
  font-size: 32px;
  font-weight: 700;
  color: #1a2440;
  margin-bottom: 12px;
}

.hero-desc {
  max-width: 640px;
  margin: 0 auto 24px;
  font-size: 14px;
  line-height: 1.8;
  color: #5f6c80;
}

.hero-actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

/* 通用按钮样式 */
.btn {
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #94B455;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.25);
}

.btn-primary:hover {
  background: #86a34b;
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.32);
}

.btn-secondary {
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.full-width {
  width: 100%;
  justify-content: center;
}

/* 通用 section 布局 */
.section {
  padding: 16px 16px 30px;
}

.section-inner {
  max-width: 80%;
  margin: 0 auto;
}

.section-title {
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 30px;
  color: #111827;
  text-align: left;
  background: linear-gradient(to right, #e7f3d4, #F5F7FB);
  padding: 10px 20px;
  border-left: 10px solid #94B455;
}


/* 功能区 */
.features-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.feature-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 18px 16px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.06);
  text-align: left;
}

.feature-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.feature-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.feature-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.feature-text {
  font-size: 13px;
  line-height: 1.7;
  color: #6b7280;
}

/* 预览区 */
.section-preview {
  padding-top: 8px;
}

.preview-wrapper {
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.2);
}

.carousel-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  border-radius: 18px;
}

/* 下载中心 */
.download-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 8px;
}

.download-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 20px 20px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  border: 1px solid #e5e7eb;
}

.download-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.download-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #e7f3d4;
  color: #5E7E2C;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 0;
}

.download-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.download-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 10px;
}

.download-meta {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  font-size: 13px;
  color: #4b5563;
}

.download-meta li {
  line-height: 1.8;
  position: relative;
  padding-left: 14px;
}

.download-meta li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #2563eb;
}

.download-extra {
  margin-top: 10px;
  font-size: 12px;
  color: #9ca3af;
}

.disclaimer {
  margin-top: 20px;
  font-size: 12px;
  line-height: 1.9;
  color: #6b7280;
  background: #fef3c7;
  border-radius: 8px;
  padding: 12px 14px;
  border-left: 4px solid #f59e0b;
}

.carousel-wrapper {
  width: 100%;
  border-radius: 18px;
  height: 400px;
  overflow: hidden;
}
/* 响应式布局：平板及以下 */
@media (max-width: 1024px) {
  .features-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .download-grid {
    grid-template-columns: 1fr;
  }
}

/* 移动端 */
@media (max-width: 768px) {
  .hero {
    padding: 36px 16px 28px;
  }

  .hero-title {
    font-size: 24px;
  }

  .hero-desc {
    font-size: 13px;
  }

  .section {
    padding: 8px 12px 32px;
  }

  .section-inner {
    max-width: 100%;
  }

  .section-title {
    font-size: 18px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .preview-wrapper {
    padding: 10px;
  }

  .preview-panel.large {
    height: 120px;
  }

  .preview-panel.small {
    height: 60px;
  }
}
</style>